/**
 * Bring the picture back when the browser's decoder gives up.
 *
 * Safari on a Mac failed to decode at a switch from a 4K original to a
 * 720p rung - "media error 3, Media failed to decode" - and the picture
 * went black for good: the position and the sound counter carried on
 * for seven minutes and nothing more was asked for. The player let it
 * be, because it only acted on errors hls.js marks fatal, and an error
 * from the video element itself carries no such mark.
 *
 * The way back is hls.js's own: detach the element and attach it again,
 * which gives it a fresh decoder. Attached again, hls.js would start
 * from where the title was opened, so it is told to carry on from where
 * the picture stopped instead - and playing again if it was playing.
 *
 * At most a few times a minute. A decoder that fails again straight
 * after is not going to be fixed this way, and the player's slower
 * remedy - rebuilding itself with fresh addresses - takes over.
 */

// MediaError.MEDIA_ERR_DECODE.
export const MEDIA_ERR_DECODE = 3;

export const RECOVERIES_PER_MINUTE = 3;

/** Whether a player error is the decoder giving up. */
export const isDecodeFailure = (detail: any) =>
  detail?.mediaError?.code === MEDIA_ERR_DECODE;

/**
 * One player's recoveries. Returns the function that attempts one, and
 * says whether it did.
 */
export const decoderRecovery = (now: () => number = Date.now) => {
  let recent: number[] = [];
  return (hls: any, note?: (text: string) => void): boolean => {
    const media: HTMLMediaElement | null | undefined = hls?.media;
    if (!hls || !media) {
      return false;
    }
    const at = now();
    recent = recent.filter((then) => at - then < 60_000);
    if (recent.length >= RECOVERIES_PER_MINUTE) {
      return false;
    }
    recent.push(at);
    const position = media.currentTime;
    const playing = !media.paused;
    hls.once('hlsMediaAttached', () => {
      hls.startLoad(position);
      if (playing) {
        media.play()?.catch?.(() => {});
      }
    });
    hls.recoverMediaError();
    note?.(
      `decoder failed at ${position.toFixed(1)}s: attached again, carrying on from there`,
    );
    return true;
  };
};
